import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent, useReducedMotion } from "framer-motion";
import { User, MessageSquareText, Search, ShieldAlert, Lightbulb, Trophy, Gavel, Rocket } from "lucide-react";

const STEPS = [
  { icon: User, t: "You", d: "What you know, have and can afford" },
  { icon: MessageSquareText, t: "Interview", d: "A guided conversation, not a form" },
  { icon: Search, t: "Research", d: "Live demand and competition" },
  { icon: ShieldAlert, t: "Challenge", d: "Assumptions stress-tested" },
  { icon: Lightbulb, t: "Opportunities", d: "Three built around you" },
  { icon: Trophy, t: "Tournament", d: "Compared until one wins" },
  { icon: Gavel, t: "Verdict", d: "An honest recommendation" },
  { icon: Rocket, t: "Action", d: "One clear next step" },
];

function useActive(progress, threshold, reduce) {
  const [on, setOn] = useState(!!reduce);
  useMotionValueEvent(progress, "change", (p) => {
    if (!reduce) setOn(p >= threshold - 0.03);
  });
  useEffect(() => {
    if (reduce) setOn(true);
  }, [reduce]);
  return on;
}

function NodeH({ step, index, total, progress, reduce }) {
  const Icon = step.icon;
  const on = useActive(progress, index / (total - 1), reduce);
  return (
    <div className="flex flex-col items-center text-center px-1" data-testid={`journey-node-${index}`} data-active={on}>
      <motion.div
        animate={{ scale: on ? 1 : 0.86 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        className={`w-[64px] h-[64px] rounded-2xl flex items-center justify-center border transition-colors duration-500 ${
          on
            ? "bg-donkey-orange/15 border-donkey-orange text-donkey-orange shadow-[0_0_30px_rgba(249,115,22,0.35)]"
            : "bg-donkey-deep border-donkey-line text-donkey-grey"
        }`}
      >
        <Icon className="w-6 h-6" />
      </motion.div>
      <p className={`mt-3 font-heading font-semibold text-sm transition-colors ${on ? "text-donkey-warm" : "text-donkey-grey"}`}>{step.t}</p>
      <p className="text-[11px] text-donkey-grey mt-1 leading-snug">{step.d}</p>
    </div>
  );
}

function NodeV({ step, index, total, progress, reduce }) {
  const Icon = step.icon;
  const on = useActive(progress, index / (total - 1), reduce);
  return (
    <div className="flex items-center gap-4" data-testid={`journey-node-m-${index}`} data-active={on}>
      <motion.div
        animate={{ scale: on ? 1 : 0.9 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        className={`w-12 h-12 rounded-xl flex items-center justify-center border shrink-0 transition-colors duration-500 relative z-10 ${
          on
            ? "bg-donkey-orange/15 border-donkey-orange text-donkey-orange shadow-[0_0_24px_rgba(249,115,22,0.35)]"
            : "bg-donkey-deep border-donkey-line text-donkey-grey"
        }`}
      >
        <Icon className="w-5 h-5" />
      </motion.div>
      <div>
        <p className={`font-heading font-semibold text-sm transition-colors ${on ? "text-donkey-warm" : "text-donkey-grey"}`}>{step.t}</p>
        <p className="text-xs text-donkey-grey leading-snug">{step.d}</p>
      </div>
    </div>
  );
}

export function ProcessJourney() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 75%", "end 55%"] });
  const fillX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const pulseLeft = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const total = STEPS.length;

  return (
    <section ref={ref} className="relative overflow-hidden border-t border-donkey-line bg-donkey-black" data-testid="process-journey">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
        <div className="w-16 h-1 rounded-full bg-donkey-orange mb-6" />
        <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-donkey-warm">Watch the Donkey work</h2>
        <p className="text-donkey-grey mt-4 max-w-2xl leading-relaxed">
          From what you already know to one clear next step. Scroll to follow the thinking.
        </p>

        {/* Desktop horizontal */}
        <div className="hidden lg:block relative mt-16">
          <div className="absolute left-0 right-0 top-8 h-0.5 bg-donkey-line" />
          <motion.div
            style={{ scaleX: reduce ? 1 : fillX, transformOrigin: "left" }}
            className="absolute left-0 right-0 top-8 h-0.5 bg-gradient-to-r from-donkey-orange to-donkey-deeporange"
          />
          {!reduce && (
            <motion.div
              style={{ left: pulseLeft }}
              className="absolute top-8 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-donkey-orange shadow-[0_0_16px_rgba(249,115,22,0.8)]"
            />
          )}
          <div className="relative grid grid-cols-8 gap-2">
            {STEPS.map((s, i) => (
              <NodeH key={s.t} step={s} index={i} total={total} progress={scrollYProgress} reduce={reduce} />
            ))}
          </div>
        </div>

        {/* Mobile vertical */}
        <div className="lg:hidden relative mt-12 pl-6">
          <div className="absolute left-[30px] top-2 bottom-2 w-0.5 bg-donkey-line" />
          <motion.div
            style={{ scaleY: reduce ? 1 : fillX, transformOrigin: "top" }}
            className="absolute left-[30px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-donkey-orange to-donkey-deeporange"
          />
          <div className="space-y-5 relative">
            {STEPS.map((s, i) => (
              <NodeV key={s.t} step={s} index={i} total={total} progress={scrollYProgress} reduce={reduce} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProcessJourney;
